import { useMemo } from 'react';
import { TInheritedProps, IDataGridConfig, IAction } from '@ts/storybook';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { IRouterProps } from '@interfaces/App.interface';
import {
  generateGlobalColumns,
  generateBooleanColumn,
  generateDateColumn,
  generateEntityColumn,
  generateMultiEntityColumn,
  SeverityColumn,
} from '@ts/storybook';
import { useNavigate } from 'react-router';
import { IConfigComponent, CWarning, CUser, CLocation, CEvent, CElement, CEventType, CCategory } from '@ts/ui-models';
import { useTheme } from '@mui/material';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';

interface IWarningsProps extends IRouterProps {
  configComponent?: IConfigComponent;
  inheritedProps?: TInheritedProps<false>;
}

export const defaultConfigComponent: IConfigComponent = {
  componentId: 'Warning',
  aclId: 'Warning',
  entity: 'warning',
  fetchURL: '/api/v3/warning',
  mainClass: CWarning,
};

const Warnings = (props: IWarningsProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { configComponent = defaultConfigComponent, inheritedProps } = props;
  const config: IDataGridConfig = {
    columnVisibilityModel: { id: false, event: false, updatedAt: false },
    sortModel: [{ field: 'updatedAt', sort: 'desc' }],
  };

  const extraAction = (): IAction => {
    return {
      id: 'viewEvent',
      label: 'View event',
      icon: 'event_note',
      onClick: handleClick => {
        const entity = new CEvent(handleClick().event);
        const titleNewRoute = `${entity?.getLabelEntity()} | ${entity?.toString()}`;

        navigate(`/event/${entity.getID()}/view`, { state: { title: titleNewRoute } });
      },
    };
  };

  const shouldEditCheckbox = (params: GridRenderCellParams): boolean => {
    const field = params.field;
    const tWarning = new CWarning(params.row);
    return tWarning.canEditStateByField(field);
  };

  const getColumns = useMemo((): GridColDef<any>[] => {
    const mixProps = { ...props, configComponent, shouldEditCheckbox };
    const globalCells = generateGlobalColumns(mixProps);
    return [
      globalCells.id(),
      SeverityColumn({ theme }),
      generateDateColumn(
        { field: 'timestamp', headerName: 'Date', isFilterable: true, isSortable: true },
        { field: 'timestamp' }
      ),
      generateMultiEntityColumn(
        { field: 'event.eventType.categories', headerName: 'Categories', isSortable: false },
        { field: 'event.eventType', tEntity: 'categories', mainClass: CCategory },
        { inheritedProps, chips: { limitChips: 20, onlyChips: true } }
      ),
      generateEntityColumn(
        { field: 'event.eventType', headerName: 'Type', isEditable: false, isSortable: false, isFilterable: false },
        { field: 'eventType', tEntity: 'event', mainClass: CEventType },
        {
          inheritedProps,
          generateSearchEntitySelector,
        }
      ),
      generateEntityColumn(
        { field: 'event.location', headerName: 'Location', isEditable: false, isSortable: false },
        { field: 'location', tEntity: 'event', mainClass: CLocation },
        {
          inheritedProps,
        }
      ),
      generateMultiEntityColumn(
        { field: 'event.relatedItems', headerName: 'Related items', isSortable: false },
        { field: 'event.relatedItems', mainClass: CElement },
        { inheritedProps }
      ),
      generateEntityColumn(
        { field: 'assignee', isSortable: false, isFilterable: false },
        { tEntity: 'assignee', mainClass: CUser },
        { generateSearchEntitySelector }
      ),
      generateBooleanColumn(
        { field: 'ack', headerName: 'Acknowledged', isSortable: true, isEditable: true, isFilterable: true },
        { field: 'ack' },
        { mixProps }
      ),
      generateBooleanColumn(
        { field: 'resolved', isSortable: true, isEditable: true, isFilterable: true },
        { field: 'resolved' },
        { mixProps }
      ),
      generateBooleanColumn(
        { field: 'closed', isSortable: true, isEditable: true, isFilterable: true },
        { field: 'closed' },
        { mixProps }
      ),
      generateEntityColumn(
        { field: 'event', headerName: 'Event', isEditable: false, isSortable: false },
        { field: 'event', mainClass: CEvent },
        { inheritedProps }
      ),
      generateDateColumn(
        { field: 'updatedAt', headerName: 'Last update', isSortable: true, isFilterable: true },
        { field: 'updatedAt' }
      ),
    ];
  }, [props, configComponent]);

  return (
    <WrapperDataGrid
      configComponent={configComponent}
      dataGridConfig={config}
      columns={getColumns}
      dataGridMode="server"
      //Extra data
      withoutDefaultActions
      extraActions={[extraAction()]}
      inheritedProps={inheritedProps}
      enabledFilters
      enabledSort
    />
  );
};

export default Warnings;
